import React, { useState, useEffect } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { FaRegTrashAlt } from 'react-icons/fa'
import {
  Btn,
  BtnIcon,
  CustomDropdown,
  CustomModal,
  InputNumeroDecimal,
  Loading,
  NumeroDecimal,
} from '../../../../../components'
import { postValorFreteConsolidado } from '../../../../../services/api/https/registration/regras-frete.http'

function round2(v) {
  return Number(Math.round(v + 'e2') + 'e-2')
}

const NENHUMA_TAXA = { label: 'Nenhuma', taxa: null }

export default function ModalEmitirCteEmpresaCliente({
  visible,
  entregas,
  // Quando a Empresa já foi escolhida no filtro da tela, o campo abre travado nela
  empresaFixa = false,
  onModalClose,
  onCadastrar,
  onError,
}) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null)
  const [clienteSelecionado, setClienteSelecionado] = useState(null)
  const [dadosFrete, setDadosFrete] = useState(null)
  const [notas, setNotas] = useState([])
  const [taxaFixa, setTaxaFixa] = useState(NENHUMA_TAXA)

  // Entregas que ainda podem compor um CTe
  const entregasElegiveis = (entregas || []).filter(
    e => e.cte_status !== 'autorizado' && e.cte_status !== 'processando_autorizacao',
  )

  const empresas = _distinctBy(entregasElegiveis, 'company_id').map(e => ({
    company_id: e.company_id,
    company_name: e.company_name,
  }))

  const clientes = empresaSelecionada
    ? _distinctBy(
        entregasElegiveis.filter(e => e.company_id === empresaSelecionada.company_id),
        'client_id',
      ).map(e => ({ client_id: e.client_id, nomeFantasia: e.nomeFantasia }))
    : []

  // Reinicia ao abrir e ao fechar: seleção mantida após fechar reabriria o modal
  // buscando o frete de um cliente que pode não estar mais na lista
  useEffect(() => {
    setEmpresaSelecionada(null)
    setClienteSelecionado(null)
    setDadosFrete(null)
    setNotas([])
    setTaxaFixa(NENHUMA_TAXA)

    if (!visible) return

    // Ao abrir, pré-seleciona a Empresa quando só existe uma
    const empresasUnicas = _distinctBy(
      (entregas || []).filter(
        e => e.cte_status !== 'autorizado' && e.cte_status !== 'processando_autorizacao',
      ),
      'company_id',
    )
    if (empresasUnicas.length === 1) {
      setEmpresaSelecionada({
        company_id: empresasUnicas[0].company_id,
        company_name: empresasUnicas[0].company_name,
      })
    }
  }, [visible, entregas])

  // Pré-seleciona o Cliente quando a Empresa selecionada tem apenas um
  useEffect(() => {
    if (!visible || !empresaSelecionada) return
    setClienteSelecionado(prev => {
      if (prev && clientes.some(c => c.client_id === prev.client_id)) return prev
      return clientes.length === 1 ? clientes[0] : null
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, empresaSelecionada])

  // Busca o valor base das notas quando Empresa + Cliente estão definidos
  useEffect(() => {
    async function fetchData(entregasSelecionadas) {
      try {
        setIsLoading(true)
        setDadosFrete(null)
        setNotas([])
        setTaxaFixa(NENHUMA_TAXA)

        const resultado = await postValorFreteConsolidado(
          entregasSelecionadas.map(e => e.delivery_id),
        )

        const notasComEntrega = resultado.notas.map(nota => {
          const entrega = entregasSelecionadas.find(e => e.delivery_id === nota.delivery_id)
          return {
            delivery_id: nota.delivery_id,
            identifier: entrega ? entrega.identifier : nota.delivery_id,
            weightTotal: entrega ? entrega.weightTotal : null,
            qte_sku: entrega ? entrega.qte_sku : null,
            valor_base: nota.valor_base,
            componentes: nota.componentes,
          }
        })

        setDadosFrete(resultado)
        setNotas(notasComEntrega)
      } catch (error) {
        onError(error)
      } finally {
        setIsLoading(false)
      }
    }
    if (!visible || !empresaSelecionada || !clienteSelecionado) return

    const entregasSelecionadas = entregasElegiveis.filter(
      e =>
        e.company_id === empresaSelecionada.company_id &&
        e.client_id === clienteSelecionado.client_id,
    )
    if (!entregasSelecionadas.length) {
      setDadosFrete(null)
      setNotas([])
      return
    }

    fetchData(entregasSelecionadas)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, empresaSelecionada, clienteSelecionado])

  // Helpers
  const taxasFixasOptions = [
    NENHUMA_TAXA,
    ...((dadosFrete && dadosFrete.taxas_fixas) || []).map(taxa => ({
      label: `${taxa.nome} - R$ ${Number(taxa.valor).toFixed(2).replace('.', ',')}`,
      taxa,
    })),
  ]

  const valorTotalBase = round2(
    notas.reduce((soma, nota) => soma + (Number(nota.valor_base) || 0), 0) +
      (taxaFixa && taxaFixa.taxa ? Number(taxaFixa.taxa.valor) : 0),
  )

  // Events
  const onRemoverNota = nota => {
    setNotas(prevState => prevState.filter(n => n.delivery_id !== nota.delivery_id))
  }

  const onBtnCadastrarClicked = () => {
    try {
      if (!empresaSelecionada) throw new Error('Selecione a Empresa')
      if (!clienteSelecionado) throw new Error('Selecione o Cliente')
      if (!notas.length) throw new Error('Mantenha pelo menos uma Nota Fiscal na tabela')

      // Consolida os componentes das notas mantidas somando os valores da mesma regra
      const consolidadosPorNome = new Map()
      notas.forEach(nota => {
        ;(nota.componentes || []).forEach(componente => {
          const existente = consolidadosPorNome.get(componente.nome)
          if (existente) existente.valor = round2(existente.valor + componente.valor)
          else consolidadosPorNome.set(componente.nome, { ...componente })
        })
      })
      const componentes = [...consolidadosPorNome.values()]

      // Regras disponíveis no modal Nova Regra de Frete: proporcionais consolidadas + taxas fixas
      const regras = [...componentes.map(c => ({ ...c })), ...((dadosFrete && dadosFrete.taxas_fixas) || [])]

      // Taxa fixa selecionada entra como componente do CTe
      if (taxaFixa && taxaFixa.taxa) {
        componentes.push({ nome: taxaFixa.taxa.nome, tipo: taxaFixa.taxa.tipo, valor: taxaFixa.taxa.valor })
      }

      onCadastrar({
        deliveries_ids: notas.map(nota => nota.delivery_id),
        frete: valorTotalBase,
        componentes,
        faixa_km_name: dadosFrete ? dadosFrete.faixa_km_name : null,
        regras,
      })
    } catch (error) {
      onError(error)
    }
  }

  // Render
  return (
    <CustomModal
      isVisible={visible}
      header={'Emitir CTe Empresa-Cliente'}
      width={750}
      onClose={onModalClose}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      <div className="p-grid">
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="company_name"
            options={empresas}
            value={
              empresaSelecionada
                ? empresas.find(e => e.company_id === empresaSelecionada.company_id) || null
                : null
            }
            label="Empresa:*"
            filter={true}
            disabled={empresaFixa}
            onChange={value => {
              setEmpresaSelecionada(value)
              setClienteSelecionado(null)
              setDadosFrete(null)
              setNotas([])
              setTaxaFixa(NENHUMA_TAXA)
            }}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="nomeFantasia"
            options={clientes}
            value={
              clienteSelecionado
                ? clientes.find(c => c.client_id === clienteSelecionado.client_id) || null
                : null
            }
            label="Cliente:*"
            filter={true}
            disabled={!empresaSelecionada}
            onChange={value => setClienteSelecionado(value)}
          />
        </div>

        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="label"
            options={taxasFixasOptions}
            value={taxasFixasOptions.find(o => o.label === taxaFixa.label) || NENHUMA_TAXA}
            label="Adicionar taxa fixa ao CTe (opcional):"
            disabled={!dadosFrete || !(dadosFrete.taxas_fixas || []).length}
            onChange={value => setTaxaFixa(value)}
          />
          {dadosFrete && !(dadosFrete.taxas_fixas || []).length ? (
            <span style={{ color: 'gray', fontSize: 12 }}>
              Nenhuma Regra de Faturamento do tipo Valor Fixo cadastrada para a Faixa de KM dessa
              cidade
            </span>
          ) : null}
        </div>

        <div className="p-col-12">
          {isLoading ? (
            <div className="text-center" style={{ marginTop: 20 }}>
              <Loading />
            </div>
          ) : (
            <DataTable
              value={notas}
              header="Notas Fiscais consideradas"
              emptyMessage="Selecione Empresa e Cliente para carregar as Notas Fiscais"
            >
              <Column field="identifier" header="Identificador" />
              <Column
                field="weightTotal"
                header="Peso"
                style={{ width: 120, textAlign: 'center' }}
                body={nota => <NumeroDecimal>{nota.weightTotal}</NumeroDecimal>}
              />
              <Column field="qte_sku" header="Qte. Sku" style={{ width: 110, textAlign: 'center' }} />
              <Column
                field="valor_base"
                header="Valor Base"
                style={{ width: 140, textAlign: 'center' }}
                body={nota => <NumeroDecimal>{nota.valor_base}</NumeroDecimal>}
              />
              <Column
                header=""
                style={{ width: 60, textAlign: 'center' }}
                body={nota => (
                  <BtnIcon onClick={() => onRemoverNota(nota)}>
                    <FaRegTrashAlt color="red" size={18} />
                  </BtnIcon>
                )}
              />
            </DataTable>
          )}
        </div>

        <div className="p-md-6 p-col-12">
          <InputNumeroDecimal
            label="Valor Total Base:"
            marginTop={10}
            value={valorTotalBase}
            onChange={() => {}}
            disabled={true}
          />
          <span style={{ color: 'gray', fontSize: 12 }}>
            Esse valor não considera impostos e pode ser ajustado posteriormente
          </span>
        </div>

        <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
          <Btn onClick={onBtnCadastrarClicked}>Cadastrar Cte</Btn>
        </div>
      </div>
    </CustomModal>
  )
}

function _distinctBy(lista, campo) {
  const vistos = new Set()
  return lista.filter(item => {
    if (vistos.has(item[campo])) return false
    vistos.add(item[campo])
    return true
  })
}
